import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, of, switchMap } from 'rxjs';
import { DevicesService, MeasurementsService, StagesService } from '@cosmos/angular/api';
import { MeasurementRefreshService, PanelService, RBACService, StateService } from '@cosmos/angular/utils';
import { Device, isMeasurementStarted, Measurement, Project, User } from '@cosmos/shared';
import { ErrorService } from '@cosmos/angular/utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EditMeasurementForm, MeasurementChangeDeviceForm, MeasurementRescheduleForm } from '../../dynamic-form/forms/measurement.form';
import { FileUploadService } from '../../../legacy/file-upload/file-upload.service';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { ReportsManagerComponent } from '../../components/reports-manager/reports-manager.component';
import { MeasurementActionsMenuComponent, MeasurementAction } from '../../components/measurement-actions-menu/measurement-actions-menu.component';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';
import { MeasurementChangeStageDialogComponent } from '../../components/measurement-change-stage-dialog/measurement-change-stage-dialog.component';
import { BreadcumbsService } from '../../navigation/breadcrumbs/breadcrumbs.service';
import { MonitoringTreeService } from '../../monitoring/monitoring-tree.service';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TemperatureInsightsCardComponent } from '../../cards/insights/temperature-insights-card/temperature-insights-card.component';
import { DynamicLineChartComponent } from '../../data/charts/dynamic-line-chart/dynamic-line-chart.component';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';

@Component({
  selector: 'cosmos-temperature-measurement',
  templateUrl: './temperature-measurement.component.html',
  imports: [TranslatePipe, TemperatureInsightsCardComponent, DynamicLineChartComponent,
    CardModule, DividerModule, ButtonModule, DynamicDialogModule, ConfirmDialogModule, MeasurementActionsMenuComponent,
    MeasurementInfoCardComponent
  ],
  providers: [DialogService, ConfirmationService, TranslateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemperatureMeasurementComponent {
  private readonly errorService = inject(ErrorService);
  readonly rbac = inject(RBACService);
  readonly dialogService = inject(DialogService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly devicesService = inject(DevicesService);
  private readonly stagesService = inject(StagesService);
  private readonly filesService = inject(FileUploadService);
  private readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly breadcumbsService = inject(BreadcumbsService);
  private readonly treeService = inject(MonitoringTreeService);
  readonly panelService = inject(PanelService);

  readonly measurementId = input<string>();

  // Signals — mutations trigger OnPush re-render
  readonly measurement = signal<Measurement | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);

  user: User;
  generatingReport = false;

  constructor() {
    this.stateService.currentUser$
      .pipe(takeUntilDestroyed())
      .subscribe(x => { this.user = x; });

    effect(() => {
      if (this.measurementId()) {
        this.initData();
      }
    });
  }

  initData(): void {
    this.measurementsService.findById(this.measurementId(), '/?populate_project&populate_device=name,deviceProfile').subscribe({
      next: (response) => {
        this.project.set(response.body.project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => this.getData(),
    });
  }

  getData() {
    this.device.set(this.measurement().device as Device || { name: '-' });
  }

  editMeasurement() {
    this.openDialog();
  }

  finishMeasurement($event: Event) {
    this.confirmationService.confirm({
      target: $event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish')
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => {
              this.initData();
              this.refreshService.trigger();
            },
          }),
        );
      },
      reject: () => null
    });
  }

  changeDevice(measurement: Measurement) {
    this.devicesService.find(`/?project=${this.measurement().project._id}&available=true&deviceType=DataAcquisition&deviceProfile=${this.device().deviceProfile}`).subscribe({
      next: (response) => {
        const devices = response.body;
        if (devices.length > 0) {
          const measurementChangeDeviceForm = new MeasurementChangeDeviceForm(this.translateService, devices, this.user, {});
          const dialogRef = this.dialogService.open(DynamicFormDialog, {
            data: { form: measurementChangeDeviceForm, dialogTitle: this.translateService.instant('measurement.change_device') },
            header: this.translateService.instant('measurement.change_device'),
            modal: true, closable: true, width: '50vw',
            breakpoints: { '960px': '75vw', '640px': '90vw' },
          });
          dialogRef.onClose.subscribe(result => {
            if (result !== undefined) {
              this.measurementsService.changeDevice(this.measurement()._id, result).subscribe(
                this.errorService.handleMutation({
                  successMessageKey: 'measurement.messages.updateSuccess',
                  onSuccess: () => this.initData(),
                }),
              );
            }
          });
        } else {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
      error: (e) => {
        if (e.error.message == 'Devices not Found') {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
    });
  }

  moveStage(measurement: Measurement) {
    this.fetchProjectStageTree(this.project()._id).subscribe({
      next: (stageTree) => {
        const dialogRef = this.dialogService.open(MeasurementChangeStageDialogComponent, {
          data: { measurement, stageTree },
          header: this.translateService.instant('measurement.move_stage.title'),
          modal: true, closable: true, width: '30vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { stage: string } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeStage(measurement._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => {
                  // The measurement's URL no longer encodes its stage
                  // (flat /monitoring/measurement/:id route), so a stage-only
                  // move doesn't change the URL at all — no navigate needed.
                  // MeasurementPage's fetch is keyed off the (unchanged)
                  // :measurement param, so it wouldn't otherwise re-run.
                  this.refreshService.trigger();
                  // The measurement moved to a different stage — the tree rail
                  // must re-parent its leaf.
                  this.treeService.refresh();
                  // Breadcrumbs are only re-resolved on real navigation; a
                  // stage-only move doesn't change the URL, so nothing else
                  // would refresh the (now stale) breadcrumb trail.
                  this.breadcumbsService.refresh();
                  // Same reason this component's own `measurement` signal
                  // (built from `measurementId()`, not from the route's stage
                  // param) needs an explicit refetch — otherwise re-opening
                  // this dialog would show the pre-move stage again.
                  this.initData();
                },
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // Fetches every root stage in the project, then each root's full
  // descendant tree — the shape the change-stage dialog's tree picker needs.
  private fetchProjectStageTree(projectId: string) {
    return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
      switchMap((rootsRes) => {
        const roots = (rootsRes.body as any[]) ?? [];
        if (roots.length === 0) return of([]);
        return this.stagesService.getForest(roots.map((r) => r._id)).pipe(map((res) => res.body ?? []));
      }),
    );
  }

  openDialog() {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project().visualizationConfig, measurementType: m.measurementType
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm },
      modal: true, closable: true,
      header: this.translateService.instant('dialog.title.edit.measurement'),
      width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe(result => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.initData(),
          }),
        );
      }
    });
  }

  downloadCSV() {
    this.measurementsService.downloadCSV('_id=' + this.measurement()._id).subscribe({
      next: (response) => { this.filesService.downloadCSVFromBlob(response); },
      error: () => {
        this.errorService.notify('error', this.translateService.instant('measurement.download.error'));
      }
    });
  }

  openRescheduleDialog(measurement: Measurement) {
    if (measurement.timeAll.length) {
      const m = this.measurement();
      const startTimestamp = m.timeAll.findIndex(time => time == measurement.time[0]);
      const endTimestamp = m.timeAll.findIndex(time => time == measurement.time[measurement.time.length - 1]);
      const rescheduleForm = new MeasurementRescheduleForm(this.translateService, measurement.timeAll, startTimestamp, endTimestamp, this.user, {});

      const dialogRef = this.dialogService.open(DynamicFormDialog, {
        data: { form: rescheduleForm },
        header: this.translateService.instant('dialog.title.reschedule'),
        modal: true, closable: true, width: '50vw',
        breakpoints: { '960px': '75vw', '640px': '90vw' },
      });

      dialogRef.onClose.subscribe(result => {
        if (result !== undefined) {
          this.measurementsService.reschedule(this.measurementId(), result.timestampStart, result.timestampEnd).subscribe(
            this.errorService.handleMutation({
              successMessageKey: 'measurement.reschedule.success',
              onSuccess: () => this.initData(),
            }),
          );
        }
      });
    } else {
      this.errorService.notify('warn', this.translateService.instant('measurement.reschedule.no-data'));
    }
  }

  openManageReports() {
    this.panelService.initialize(ReportsManagerComponent, { measurement: this.measurement() });
  }

  unstartMeasurement(event?: Event) {
    this.confirmationService.confirm({
      ...(event ? { target: event.currentTarget as EventTarget } : {}),
      message: this.translateService.instant('measurement.unstart-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('measurement.unstart')
      },
      accept: () => {
        this.measurementsService.unstart(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.unstartSuccess',
            // Refreshes the parent page's `measurement` signal, since its
            // viewMode depends on `started` and won't otherwise notice this
            // component flipped it — see MeasurementRefreshService.
            onSuccess: () => this.refreshService.trigger(),
          }),
        );
      },
      reject: () => null
    });
  }

  readonly tier2Actions = computed<MeasurementAction[]>(() => {
    const m = this.measurement();
    const actions: MeasurementAction[] = [
      { labelKey: 'measurement.download-csv', icon: 'fa-solid fa-download', action: () => this.downloadCSV() },
      { labelKey: 'measurement.manage-reports', icon: 'fa-solid fa-file-lines', action: () => this.openManageReports() },
      { labelKey: 'measurement.move_stage.action', icon: 'fa-solid fa-layer-group', action: () => this.moveStage(m) },
    ];
    if (m?.active) {
      actions.push({ labelKey: 'measurement.reschedule.button', icon: 'fa-solid fa-clock-rotate-left', action: () => this.openRescheduleDialog(m) });
      if (isMeasurementStarted(m)) {
        actions.push({ labelKey: 'measurement.unstart', icon: 'fa-solid fa-rotate-left', action: () => this.unstartMeasurement() });
      }
    }
    return actions;
  });
}
